import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const isMobile = window.matchMedia('(max-width: 750px)').matches;
    
    if (isMobile) {
      this.setAttribute('data-disabled', '');
      this.#disableOnMobile();
    } else {
      this.#addRepeatedItems();
      this.#duplicateContent();
      setTimeout(() => {
        this.#setSpeed();
      }, 100);
    }

    window.addEventListener('resize', this.#handleResize);
    
    if (!this.closest('.hero')) {
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 36;
    const { marqueeItems } = this.refs;
    
    const marqueeWidth = this.offsetWidth || this.parentElement?.offsetWidth || window.innerWidth || 1;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    
    if (marqueeRepeatedItemWidth === 0) return speedFactor;
    
    const count = Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
    const speed = Math.sqrt(count) * (speedFactor / 1.5);
    
    return Math.max(speed, 5);
  }

  #handleResize = debounce(() => {
    const isMobile = window.matchMedia('(max-width: 750px)').matches;
    
    if (isMobile) {
      if (!this.hasAttribute('data-disabled')) {
        this.setAttribute('data-disabled', '');
        this.#disableOnMobile();
      }
    } else {
      if (this.hasAttribute('data-disabled')) {
        this.removeAttribute('data-disabled');
        this.#enableOnDesktop();
      }
    }
  }, 250);

  #disableOnMobile() {
    const { wrapper, content, marqueeItems } = this.refs;
    
    // Supprimer le clone
    this.clonedContent?.remove();
    
    // Garder seulement l'élément original
    if (marqueeItems.length > 1) {
      for (let i = marqueeItems.length - 1; i > 0; i--) {
        marqueeItems[i].remove();
      }
    }
    
    // Désactiver l'animation
    const animations = wrapper.getAnimations();
    animations.forEach(animation => animation.cancel());
    
    // Styles mobile
    wrapper.style.animation = 'none';
    wrapper.style.transform = 'none';
    content.style.flexWrap = 'wrap';
    content.style.whiteSpace = 'normal';
    content.style.gap = 'var(--marquee-gap)';
    
    // S'assurer que les éléments s'affichent correctement
    if (marqueeItems[0]) {
      marqueeItems[0].style.flexWrap = 'wrap';
      marqueeItems[0].style.whiteSpace = 'normal';
    }
  }

  #enableOnDesktop() {
    const { wrapper, content, marqueeItems } = this.refs;
    
    // Réinitialiser les styles mobile
    content.style.flexWrap = '';
    content.style.whiteSpace = '';
    wrapper.style.animation = '';
    wrapper.style.transform = '';
    
    if (marqueeItems[0]) {
      marqueeItems[0].style.flexWrap = '';
      marqueeItems[0].style.whiteSpace = '';
    }
    
    // Recréer la structure desktop
    if (marqueeItems.length === 1) {
      this.#addRepeatedItems();
      this.#duplicateContent();
    }
    
    this.#setSpeed();
    this.#restartAnimation();
  }

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) {
        if (animation.effect) {
          animation.currentTime = 0;
        }
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();
    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    for (let i = 0; i < numberOfCopies; i++) {
      if (content.children.length > 1) {
        content.lastElementChild?.remove();
      }
    }
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth || this.parentElement?.offsetWidth || window.innerWidth || 1;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 1;
    return marqueeRepeatedItemWidth === 0 ? 1 : Math.max(1, Math.ceil(marqueeWidth / marqueeRepeatedItemWidth));
  }
}

function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;
  let frameId = null;

  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      frameId = requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  frameId = requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
      if (frameId) {
        cancelAnimationFrame(frameId);
        frameId = null;
      }
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}

export default MarqueeComponent;